import { PreReservationDto } from '@orcar/common';
import {
  ALERT_MESSAGE_TYPE,
  Button,
  getPreReservation,
  INSURANCE_TYPE,
  PAGE_NAME,
  SESSION_STORAGE_KEY,
  Styles,
  useDetectMobile,
  useGetReservationsByUser,
} from '@orcar/common-d2c';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { InfoConfirmModal } from '@/components/header';
import { CAR_SHEET_COMPANY } from '@/constants/common';
import { getInsurancePerDay, getRentalDays } from '@/constants/experiment';
import {
  identifyUser,
  setUserProperties,
  trackReservationCreated,
} from '@/utils/analytics';
import * as S from '../styles';

const ConfirmPaymentPage = () => {
  const navigate = useNavigate();

  const { isMobile, screenHeight } = useDetectMobile();

  // 기존 예약 목록 가져오기
  const { data: existingReservations } = useGetReservationsByUser({
    enabled: false, // 수동으로 호출
  });

  const preReservationId = sessionStorage.getItem(
    SESSION_STORAGE_KEY.PRE_RESERVATION_ID,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preReservationData, setPreReservationData] =
    useState<PreReservationDto>();

  const handleConfirmOrder = () => {
    navigate(
      `${PAGE_NAME.reservation_detail}/${preReservationData?.reservation?.id}`,
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!preReservationId) {
      alert(ALERT_MESSAGE_TYPE.NOT_ALLOWED_ACCESS);
      navigate(PAGE_NAME.index);
      return;
    }

    getPreReservation(preReservationId)
      .then((res) => {
        if (!res.reservation) {
          alert(ALERT_MESSAGE_TYPE.INVALID_RESERVATION);
          navigate(PAGE_NAME.index);
          return;
        }
        setPreReservationData(res);

        // 예약 생성 이벤트 트래킹
        // 완전면책 가격 실험 측정: 완료 시점의 가입 여부·관측 가격·대여일수를 스냅샷으로 남긴다.
        // (매출·기대이익은 이 완료 이벤트 기준으로만 집계한다 — 선택 이벤트는 의향 지표)
        const confirmRentalDays = getRentalDays(res.pickUpAt, res.dropOffAt);
        const isSuperSelected =
          res.isFullCoverage || res.insuranceName === INSURANCE_TYPE.SUPER;
        trackReservationCreated({
          reservation_id: res.reservation.id,
          car_id: String(res.vehicleModelId),
          car_type: res.vehicleModel?.type || '',
          total_amount: res.price,
          is_with_pet: res.isWithPet,
          pet_type: res.petType,
          pet_count: res.petCount,
          base_insurance: res.insuranceName,
          insurance_selected: isSuperSelected ? 'super' : 'base',
          rental_days: confirmRentalDays,
          insurance_price_total:
            res.pricingVersion === 1
              ? res.insurancePrice
              : res.fullCoverageInsurancePrice,
          insurance_price_per_day: getInsurancePerDay(
            res.pricingVersion === 1
              ? res.insurancePrice
              : res.fullCoverageInsurancePrice,
            confirmRentalDays,
          ),
          is_member: res.d2cUserId != null,
        });

        // 사용자 식별 및 프로퍼티 업데이트
        if (res.customerName) {
          // 기존 예약 건수 계산 (현재 예약 포함)
          const currentReservationCount =
            (existingReservations?.length || 0) + 1;

          identifyUser(res.customerName, {
            customer_name: res.customerName,
            phone_number: res.customerPhoneNumber,
            reservation_count: currentReservationCount,
            last_reservation_date: new Date().toISOString(),
            total_spent: res.price,
          });

          setUserProperties({
            has_reservation: true,
            reservation_count: currentReservationCount,
            last_reservation_date: new Date().toISOString(),
            total_spent: res.price,
          });
        }
      })
      .catch(() => {
        alert(ALERT_MESSAGE_TYPE.INVALID_RESERVATION);
        navigate(PAGE_NAME.index);
        return;
      });
  }, [preReservationId]);

  if (!preReservationData || !preReservationData?.reservation) return null;

  return (
    <S.Wrapper isMobile={isMobile} screenHeight={screenHeight - 148}>
      <S.Text type='main' isMobile={isMobile}>
        예약이 완료되었습니다
      </S.Text>

      <S.Text type='sub' isMobile={isMobile}>
        {`${preReservationData.customerName}님,`}
      </S.Text>
      <S.Text type='sub' isMobile={isMobile}>
        제주에서 즐거운 시간 보내세요!
      </S.Text>
      <S.Text type='number' isMobile={isMobile}>
        예약 번호: {preReservationData.reservation?.id}
      </S.Text>
      <S.ButtonWrapper isMobile={isMobile}>
        <Button
          borderRadius='5px'
          font={Styles.FONT.H4}
          width={isMobile ? '90%' : '384px'}
          height='59px'
          bgColor={Styles.COLOR.WHITE}
          color={Styles.COLOR.GREY_02}
          borderColor={Styles.COLOR.GREY_02}
          onClick={() => setIsModalOpen(true)}
        >
          유모차/카시트 대여 안내
        </Button>
        <Button
          borderRadius='5px'
          font={Styles.FONT.H4}
          width={isMobile ? '90%' : '384px'}
          height='59px'
          onClick={handleConfirmOrder}
        >
          주문 확인하기
        </Button>
      </S.ButtonWrapper>
      {isModalOpen && (
        <InfoConfirmModal
          onClose={() => setIsModalOpen(false)}
          callNumber={`${preReservationData.companyName} 한정 카시트 및 유모차 대여 서비스`}
        >
          <OuterWrapper>
            <Text>저렴한 가격으로 편리하게 이용하세요!</Text>

            <InnerWrapper margin='12px 0 0'>
              <Text fontWeight={600} margin='0 0 4px 0'>
                꾸러기 유모차 예약 방법:
              </Text>
              <Text>
                전화 예약:{' '}
                <Link
                  href={`tel:+82${CAR_SHEET_COMPANY.tel.replace(/-/g, '')}`}
                >
                  {CAR_SHEET_COMPANY.tel}
                </Link>
              </Text>
              <Text>
                온라인 예약:{' '}
                <Link
                  href={CAR_SHEET_COMPANY.url}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {CAR_SHEET_COMPANY.url}
                </Link>
              </Text>
            </InnerWrapper>
            <InnerWrapper>
              <Text fontWeight={600} margin='0 0 4px 0'>
                {`${preReservationData.companyName} 고객 우대 혜택:`}
              </Text>
              <Text>사전 장착 서비스 무료 제공</Text>
              <Text>반납 시 차량 내에 그대로 두시면 직접 수거</Text>
            </InnerWrapper>
          </OuterWrapper>
        </InfoConfirmModal>
      )}
    </S.Wrapper>
  );
};

export default ConfirmPaymentPage;

const Link = styled.a`
  color: ${Styles.COLOR.BLUE};
  text-decoration: underline;
`;

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InnerWrapper = styled.div<{ margin?: string }>`
  display: flex;
  flex-direction: column;
  ${({ margin }) => margin && `margin: ${margin};`}
`;

const Text = styled.p<{
  margin?: string;
  fontSize?: number;
  fontWeight?: number;
}>`
  font-size: ${({ fontSize }) => (fontSize ? fontSize : 14)}px;
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};
  ${({ margin }) => margin && `margin: ${margin};`}
`;
